import { useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AuthContext } from '@urbanx/agx.auth.client';
import { StateEmailParagraphState } from '@urbanx/agx.agencies.client';
import {
  AgxButton,
  AgxTypography,
  AgxModal,
  AgxSpacer,
  Icon_Email,
  type AgxDialogControlProps,
} from '@urbanx/agx.ui-components2';
import { tokens } from '@urbanx/agx.ui-components2/tokens';
import LexicalEditor from 'components/lexical-editor/LexicalEditor';
import GetSendGridTemplate from 'services/functions/notification/getSendGridTemplate';
import EmailPreview from 'pages/custom-paragraph/EmailPreview';
import './EmailParagraphDialog.scss';
import {
  EmailParagraphForm,
  type EmailType,
  previewTemplateName,
  renderEmailPreview,
} from './emailTemplateFormDefinition';

interface Props extends AgxDialogControlProps {
  title: string;
  emailType: EmailType;
  paragraph: string;
  state: StateEmailParagraphState;
  onApply: (paragraph: string) => void;
}

export default function EmailParagraphDialog({
  title,
  emailType,
  paragraph,
  state,
  onApply,
  isOpen,
  setIsDialogOpen,
}: Props) {
  const { getAuthToken } = useContext(AuthContext);
  const form = EmailParagraphForm.useForm({
    defaultValues: { paragraph },
    onSubmit: async ({ parsedValues }) => {
      onApply(parsedValues.paragraph);
      setIsDialogOpen(false);
      return { status: 'accepted' };
    },
  });
  const templateName = previewTemplateName(emailType, state);
  const preview = useQuery({
    queryKey: ['email-preview', templateName],
    queryFn: () => GetSendGridTemplate(getAuthToken, templateName),
    enabled: isOpen,
  });
  const content = preview.data?.versions.find((version) => version.active === 1)?.plain_content;

  return (
    <AgxModal
      id="email-paragraph-dialog"
      isOpen={isOpen}
      setIsDialogOpen={setIsDialogOpen}
      title={title}
      titleIcon={<Icon_Email color={tokens.fg.action} width={30} height={30} />}
      type="split"
      variant="withDivider"
      actionButtonContainerStyle="withoutDivider"
      maxWidth="min(90vw, 1134px)"
      actionButtons={
        <AgxSpacer direction="horizontal" contentJustification="center" gap="spacing-lg" fill>
          <AgxButton
            id="cancel-email-paragraph"
            size="large"
            text="Cancel"
            variant="hollow"
            onClick={() => setIsDialogOpen(false)}
          />
          <AgxButton
            id="apply-email-paragraph"
            size="large"
            text="Apply"
            variant="primary"
            onClick={() => void form.submit()}
          />
        </AgxSpacer>
      }
      content={
        <EmailParagraphForm.Root form={form}>
          <AgxSpacer
            gap="spacing-2xl"
            padding={{ top: 'md', bottom: 'md', left: 'xl', right: 'xl' }}
            className="agx-email-paragraph-dialog-content"
          >
            <AgxSpacer direction="horizontal" wrap gap="spacing-5xl">
              <AgxSpacer
                direction="vertical"
                gap="spacing-lg"
                className="agx-email-paragraph-dialog-column"
              >
                <EmailParagraphForm.CustomField name="paragraph">
                  {(field) => (
                    <>
                      <AgxSpacer flexShrink={0} className="agx-email-paragraph-dialog-editor">
                        <LexicalEditor
                          id="email-custom-paragraph"
                          label="Custom paragraph"
                          defaultValue={paragraph}
                          maxLength={1500}
                          placeholder="Enter custom paragraph"
                          stretch
                          onChange={(html, text) => field.onChange(text.trim() ? html : '')}
                          disabled={field.disabled}
                          invalid={field.hasError}
                          ariaDescribedBy={field.errorText ? 'paragraph-error' : undefined}
                        />
                      </AgxSpacer>
                      {field.errorText && (
                        <AgxTypography id="paragraph-error" type="form_error" role="alert">
                          {field.errorText}
                        </AgxTypography>
                      )}
                    </>
                  )}
                </EmailParagraphForm.CustomField>
              </AgxSpacer>
              <AgxSpacer
                direction="vertical"
                gap="spacing-md"
                className="agx-email-paragraph-dialog-column"
              >
                {preview.isPending && (
                  <AgxTypography type="body" role="status">
                    Loading preview…
                  </AgxTypography>
                )}
                {preview.isError && (
                  <AgxTypography type="form_error" role="alert">
                    Unable to load the email preview. You can still edit and apply your paragraph.
                  </AgxTypography>
                )}
                {preview.isSuccess && !content && (
                  <AgxTypography type="body" role="status">
                    No active plain-text preview is available.
                  </AgxTypography>
                )}
                {content && (
                  <EmailPreview
                    templateContent={renderEmailPreview(content, form.values.paragraph)}
                  />
                )}
              </AgxSpacer>
            </AgxSpacer>
            <AgxTypography type="body_ui" size="small" color={tokens.text.secondary}>
              Apply updates your draft. Select Save Changes on the page to save it. Preview
              placeholders are filled with campaign details when the email is sent.
            </AgxTypography>
          </AgxSpacer>
        </EmailParagraphForm.Root>
      }
    />
  );
}
